import { ConfirmDialog, Icon, OutlinedInput } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { MenuItem, Select, Stack, Typography } from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { ValidatedInput } from '../validated-input';
import * as Styled from './styles';
import * as SX from './sx';
import type { EditInformationProps as Props } from './types';
import { validators } from './validators';

export const EditInformation = memo(({ className, disabled }: Props) => {
  const state = useBoolean();
  const [isChanged, setIsChanged] = useState(false);
  const markChanged = useCallback(() => setIsChanged(true), []);

  const handleOpenDialog = () => {
    if (!disabled) {
      state.onTrue();
    }
  };

  const handleConfirm = () => {
    setIsChanged(false);
    state.onFalse();
  };

  return (
    <>
      <Styled.Wrapper
        className={classnames('EditInformation', className)}
        sx={SX.root}
        variant="outlined"
        size="small"
        onClick={handleOpenDialog}
        disabled={disabled}
      >
        <Icon name="edit-outline" size={16} />
      </Styled.Wrapper>

      <ConfirmDialog
        state={state}
        onCancel={state.onFalse}
        onOk={handleConfirm}
        title={
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Icon name="edit-outline" size={24} color="primary.400" />
            <Typography variant="body2" color="grey.800" fontWeight={600}>
              Edit information
            </Typography>
          </Stack>
        }
        content={
          <Stack gap={1}>
            <Stack>
              <Typography variant="body4" color="grey.500" fontWeight={400}>
                Account name
              </Typography>
              <OutlinedInput
                defaultValue="Casey Strobelt"
                placeholder="No name added."
                size="medium"
                fullWidth
                disabled
                InputProps={{ readOnly: true }}
              />
            </Stack>
            <Stack>
              <Typography variant="body4" color="grey.500" fontWeight={400}>
                Email Address
              </Typography>
              <OutlinedInput
                defaultValue="Caseystrobelt@ai-bees.io"
                placeholder="No email added."
                size="medium"
                fullWidth
                disabled
                InputProps={{ readOnly: true }}
              />
            </Stack>
            <Stack direction="row" gap={1}>
              <ValidatedInput
                fieldLabel="Phone Number"
                placeholder="No phone number added."
                size="medium"
                fullWidth
                validator={validators.phone}
                defaultValue="+11455141928"
                onChange={markChanged}
              />
              <Stack gap={0.5}>
                <Typography variant="body4" color="grey.500" fontWeight={400}>
                  Role
                </Typography>
                <Select
                  value="owner"
                  variant="outlined"
                  size="medium"
                  disabled
                  displayEmpty
                  fullWidth
                  renderValue={() => 'Owner'}
                  sx={{
                    width: 200,
                  }}
                >
                  <MenuItem value="owner">Owner</MenuItem>
                </Select>
              </Stack>
            </Stack>
            <ValidatedInput
              fieldLabel="LinkedIn URL"
              placeholder="Enter LinkedIn profile URL."
              size="medium"
              fullWidth
              validator={validators.linkedin}
              onChange={markChanged}
            />

            <ValidatedInput
              fieldLabel="Twitter (X) URL"
              placeholder="Enter Twitter (X) profile URL."
              size="medium"
              fullWidth
              validator={validators.twitter}
              onChange={markChanged}
            />

            <ValidatedInput
              fieldLabel="Facebook URL"
              placeholder="Enter Facebook profile URL."
              size="medium"
              fullWidth
              validator={validators.facebook}
              onChange={markChanged}
            />

            <ValidatedInput
              fieldLabel="Instagram URL"
              placeholder="Enter Instagram profile URL."
              size="medium"
              fullWidth
              validator={validators.instagram}
              onChange={markChanged}
            />
          </Stack>
        }
        okTitle="Save"
        cancelTitle="Cancel"
        okProps={{
          variant: 'contained',
          color: 'primary',
          disabled: !isChanged,
        }}
        cancelProps={{ variant: 'text', color: 'inherit' }}
        titleSx={SX.root}
        actionsSx={SX.actions}
        headerSx={SX.header}
        maxWidth="md"
      />
    </>
  );
});

EditInformation.displayName = 'EditInformation';
